import type { RouteLocation, RouteLocationRaw, RouteRecordRaw } from 'vue-router'

/**
 * A queue item's old per-item path. The web app has no per-item screen: the
 * queue's own items list (`QueueItemsView.vue`) is where the item is shown and
 * worked, so the item id is dropped and the queue code kept.
 */
export function queueItemRedirect(to: RouteLocation): RouteLocationRaw {
  return { name: 'rules-queue-items', params: { code: String(to.params['code'] ?? '') } }
}

/**
 * The procurement agent's old per-vendor scorecard path. The scorecards screen
 * (`ScorecardsView.vue`) reads `?vendorId=` and narrows itself to that vendor.
 */
export function vendorScorecardRedirect(to: RouteLocation): RouteLocationRaw {
  const vendorId = String(to.params['id'] ?? '')
  return { path: '/procurement/vendor-scorecards', query: { vendorId } }
}

/**
 * Server-emitted deep links that pointed at routes the web app never had
 * (DL-SWEEP, 2026-10-27 — `docs/fsd-audit/2026-10-26/r0/DEADLINKS.md`).
 *
 * Every emitter was corrected at the source, but the old strings are already
 * STORED: notifications and attention items persist their `link`, AI briefings
 * persist their citations, daily operating reports persist `drill_link`, close
 * runs snapshot their readiness links, and the approval email has been sent. So
 * each old path keeps landing on the screen that shows the same thing.
 *
 * A string redirect carries the old query across (vue-router keeps `to.query`),
 * which is what makes `/inventory/ledger?warehouseId=…&from=…&to=…` land on the
 * stock ledger already filtered — that view reads all three. Named redirects
 * carry `to.params` across, so the target's param must have the same name.
 *
 * `server/internal/conformance/deeplinks_test.go` checks every link the server
 * emits against this route table, so a new dead link fails the Go build.
 */
const DEAD_DEEP_LINK_REDIRECTS: RouteRecordRaw[] = [
  // Attention items (approval tasks) — the inbox has no per-task or per-request
  // deep select; it opens on the caller's pending list.
  { path: '/approvals/inbox', redirect: '/approvals' },
  // Approval notifications, the approval-assigned email, the exceptions view.
  { path: '/approvals/:id(\\d+)', redirect: '/approvals' },
  // Attention items (reconciliation queue items).
  { path: '/controls/queues/:code([a-z0-9_.]+)/items/:id(\\d+)', redirect: queueItemRedirect },
  // Daily operating report drill + the exceptions view.
  { path: '/close/reconciliation-queues', redirect: '/controls/queues' },
  { path: '/close/reconciliation-queues/:id(\\d+)', redirect: '/controls/queues' },
  // AI briefing citations.
  { path: '/cash/forecast/runs/:id(\\d+)', redirect: { name: 'treasury-forecast' } },
  { path: '/gl/journals/:id(\\d+)', redirect: { name: 'gl-journal-entry' } },
  { path: '/close/periods/:id(\\d+)', redirect: '/close/readiness' },
  { path: '/procurement/vendors/:id(\\d+)/scorecard', redirect: vendorScorecardRedirect },
  { path: '/rvm/accuracy/:id(\\d+)', redirect: '/rvm/accuracy' },
  // AI notifications (config anomalies, autonomy recommendations).
  { path: '/ai/config-anomalies/:id(\\d+)', redirect: '/ai/config-anomalies' },
  { path: '/ai/recommendations/:id(\\d+)', redirect: '/ai/recommendations' },
  // Close obligations and readiness (snapshotted on close runs).
  { path: '/interco/statements/:id(\\d+)', redirect: '/interco/statements' },
  { path: '/opcal/business-days/board', redirect: '/operations/day-board' },
  { path: '/pos/day-closes', redirect: '/pos/day-close' },
  { path: '/procurement/reports/gr-ir', redirect: '/procurement/reports/gr-ir-aging' },
  { path: '/treasury/funds-in-transit', redirect: '/cash/funds-in-transit' },
  { path: '/treasury/debt-facilities', redirect: '/treasury/facilities' },
  { path: '/treasury/debt-facilities/covenants', redirect: '/treasury/covenants' },
  // Go-live checklist (emailed) and the treasury-officer drift notification.
  { path: '/settings/users', redirect: '/users' },
  { path: '/settings/roles', redirect: '/roles' },
  // DSR due-date notification.
  { path: '/party/data-subject-requests/:id(\\d+)', redirect: { name: 'party-dsr' } },
  // Daily operating report drills.
  { path: '/inventory/ledger', redirect: '/inventory/stock-ledger' },
  { path: '/hr/rosters', redirect: '/roster/rosters' },
  { path: '/pms/revenue', redirect: '/pms/reports' },
]

/**
 * The v1 cash screens FT4 removed (R030/R047/R062): one-step internal
 * transfers, the live cash position and the live 13-week forecast. Bookmarks,
 * favourites and recents may still hold these paths.
 *
 * `/cash/forecast/:rest+` catches every sub-path (one or more segments) except
 * `/cash/forecast/runs/:id`, whose static `runs` segment ranks higher and keeps
 * its own named redirect to the matching forecast run (DEAD_DEEP_LINK_REDIRECTS).
 */
const LEGACY_CASH_REDIRECTS: RouteRecordRaw[] = [
  { path: '/cash/transfers', redirect: '/cash/transfer-instructions' },
  { path: '/cash/transfers/new', redirect: '/cash/transfer-instructions' },
  // A v1 transfer IS a bank_transfers row, read by the same id through the
  // instruction screen (status `confirmed`), so a bookmark lands on that document.
  { path: '/cash/transfers/:id(\\d+)', redirect: { name: 'cash-transfer-instruction' } },
  { path: '/cash/position', redirect: '/treasury/positions' },
  { path: '/cash/forecast', redirect: '/treasury/forecasts' },
  { path: '/cash/forecast/:rest+', redirect: '/treasury/forecasts' },
]

/**
 * The budget model screens FT7-U22 replaced with Budget Cycles (FSD 2.2.5 F06).
 * A cycle is the budget model row, so an id carries over unchanged: a bookmark or a
 * stored link to `/budget/models/7` lands on cycle 7. The named redirect carries
 * `to.params` across, so the target's param is `id` as well. FT11-UC adds the
 * company budget policy screen, which Control Levels replaced (`?companyId=` is kept).
 */
const LEGACY_BUDGET_REDIRECTS: RouteRecordRaw[] = [
  { path: '/budget/models', redirect: '/budget/cycles' },
  { path: '/budget/models/:id(\\d+)', redirect: { name: 'budget-cycle' } },
  // FT11-UC (FSD 2.2.5 F06 V06): the company-level budget policy screen is gone; the
  // per-cost-centre Control Levels screen holds the company default and replaces it.
  { path: '/budget/policy', redirect: '/budget/control-levels' },
]

/**
 * Old paths whose screen was removed, kept alive as redirects to the FSD screen
 * that replaced them.
 *
 * C17 (operator, 2026-10-25: "remove the legacy build stuff; erpx as per the
 * FSD") removed two pre-FSD screens outright — the companies list
 * (`org/CompaniesListView.vue`, superseded by FSD 1.2.3 Legal Entities) and the
 * divisions list (`org/DivisionsView.vue`, superseded by FSD 1.2.2 Divisions).
 *
 * R0 (FSD Part 1 menu parity — one implementation per concept) removed five
 * more, each replaced by a screen in the reference menu:
 *  - the fiscal-periods screen (`org/FiscalPeriodsView.vue`) by Period Control
 *    (`/enterprise/period-control`, FSD 1.2.7, F-6.007);
 *  - the go-live checklist (`org/GoLiveChecklistView.vue`) by the Enterprise
 *    Dashboard & Inbox (`/enterprise`), which renders the same checklist data;
 *  - the FX rates screen (`fx/FxRatesView.vue`) by Currency Rates
 *    (`/enterprise/currency-rates`, FSD 1.2.7.10);
 *  - the naming-templates and headcount screens by the one "Model, Naming &
 *    Headcount" screen (`/enterprise/workforce/model`, FSD 1.2.5), which hosts
 *    both as tabs.
 *
 * Their route records are gone; the paths are not, because the server has
 * already put some of them into links it cannot take back.
 * `internal/org/readiness.go` emitted `/settings/organization/companies` and
 * `/settings/organization/fiscal-periods` as go-live checklist deep links until
 * DL-SWEEP (they were emailed, put on briefings and stored on attention items,
 * and it now emits the replacement paths), so those paths have to keep landing
 * somewhere real — here, on the screen that replaced them. The rest are
 * bookmarks and favourites a user may still hold.
 *
 * Two paths that C14 W3 also redirected are deliberately NOT here any more:
 *
 *  - `/settings/organization/hierarchy` (`org/HierarchyView.vue`) is the only
 *    screen that maintains branches and profit centres; it is kept live as the
 *    FSD 1.2.5 menu item `org-units`.
 *  - `/settings/organization/accounts` (`org/AccountsView.vue`) is the only
 *    screen that edits GL accounts and applies a COA template; it is kept live
 *    as the FSD 1.2.7 menu item `org-chart-of-accounts`.
 *
 * (DECISIONS.md, C17.) A redirect on either path would hijack its menu item now
 * that this table is spread ahead of the records it shares paths with — see the
 * comment where `router/index.ts` spreads it.
 *
 * Invariant: no route record anywhere in the table may share a path with an
 * entry here. vue-router 4 inserts an equal-score matcher AFTER the ones already
 * registered and resolves to the first match, so a record with the same path
 * declared earlier silently wins and the redirect never fires — which is
 * exactly how all four of C14's redirects were dead until C17.
 * `legacyRedirects.test.ts` builds the real table and asserts it.
 */
export const LEGACY_ORGANIZATION_REDIRECTS: RouteRecordRaw[] = [
  // FSD 1.2.3 Legal Entities — Companies had no detail route (list only), so
  // this is a straight one-to-one redirect.
  { path: '/settings/organization/companies', redirect: '/enterprise/legal-entities' },

  // FSD 1.2.2 Divisions — list only, both old and new; straight redirect.
  { path: '/settings/organization/divisions', redirect: '/enterprise/divisions' },

  // FSD 1.2.7 Period Control (R0). A server-emitted go-live deep link.
  { path: '/settings/organization/fiscal-periods', redirect: '/enterprise/period-control' },

  // FSD Part 1 overview — the Dashboard & Inbox carries the go-live checklist (R0).
  { path: '/settings/organization/go-live', redirect: '/enterprise' },

  // FSD 1.2.7.10 Currency Rates (R0).
  { path: '/fx/rates', redirect: '/enterprise/currency-rates' },

  // FSD 1.2.5 Model, Naming & Headcount (R0) — both former screens are tabs
  // there, selected by `?tab=` (WorkforceModelView.vue reads it).
  {
    path: '/enterprise/workforce/naming-templates',
    redirect: { path: '/enterprise/workforce/model', query: { tab: 'naming' } },
  },
  {
    path: '/enterprise/workforce/headcount',
    redirect: { path: '/enterprise/workforce/model', query: { tab: 'headcount' } },
  },

  // FSD 2.2.6 (FT3) — the combined two-tab cost reports page is two screens now.
  { path: '/cost/reports', redirect: '/cost/reports/absorption-variance' },

  // FSD 2.2.8 F08 V06 (FT17-B46) — GL allocation rules are retired into cost cycles
  // (migration 000281 converted every rule into a draft one-step cycle). Journals the old
  // rules posted keep drilling here through gl/drilldown.ts, and bookmarks may still hold it.
  { path: '/gl/allocation-rules', redirect: '/cost/cycles' },

  // FSD 3.2.1 / 3.2.4 (FT4, R030/R047/R062) — the v1 one-step transfers, cash
  // position and live 13-week forecast are gone with their server routes. A v1
  // transfer id is not an instruction id, so every transfer path lands on the
  // instructions list; there is no "new instruction" route (the list creates).
  ...LEGACY_CASH_REDIRECTS,

  // FSD 2.2.5 (FT7-U22) — a budget model is a Budget Cycle now.
  ...LEGACY_BUDGET_REDIRECTS,

  // Server deep links that never had a route (see DEAD_DEEP_LINK_REDIRECTS).
  ...DEAD_DEEP_LINK_REDIRECTS,
]
